// src/components/phase05/depotInjection/PrintDepotInjection.jsx
import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchDepotInjectionChart } from "../../../store/slices/depotInjectionChartsSlice";
import PrintWrapper, { printFooterStyles } from "../../common/PrintWrapper";
import logo from "../../../assets/images/logo-samplee.png";

export default function DepotInjectionPrint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { currentDepotInjectionChart, isLoading, error } = useSelector(
    (state) => state.depotInjectionCharts
  );


  /* ────────────────────── FETCH RECORD ────────────────────── */
  useEffect(() => {
    if (id) dispatch(fetchDepotInjectionChart(id));
  }, [dispatch, id]);

  /* ────────────────────── AUTO PRINT ────────────────────── */
  useEffect(() => {
    if (currentDepotInjectionChart) {
      const timer = setTimeout(() => window.print(), 500);
      window.onafterprint = () => navigate(-1);
      return () => {
        clearTimeout(timer);
        window.onafterprint = null;
      };
    }
  }, [currentDepotInjectionChart, navigate]);

  /* ────────────────────── STATES ────────────────────── */
  if (isLoading) return <div className="p-4 text-center">Loading…</div>;
  if (error) return <div className="p-4 text-danger text-center">{error}</div>;
  if (!currentDepotInjectionChart)
    return <div className="p-4 text-center">No record found.</div>;

  const d = currentDepotInjectionChart.data;
  const rows = d.injection_records || [];
  console.log(currentDepotInjectionChart);

  /* ────────────────────── DATE FORMATTER ────────────────────── */
  const fmt = (dateStr) =>
    dateStr
      ? new Date(dateStr).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      : "";

  const fmtDateTime = (dateStr) =>
    dateStr
      ? new Date(dateStr).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }) +
      " " +
      new Date(dateStr).toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })
      : "";

  return (
    <PrintWrapper>
      {/* ────────────────────── PRINT STYLES ────────────────────── */}
      <style jsx global>{`
        * {
          box-sizing: border-box !important;
          font-family: Arial, Helvetica, sans-serif !important;
        }
        body { background: #fff !important; color: #000 !important; margin: 0 !important; padding: 0 !important; }

        .print-container { padding: 20px !important; position: relative !important; }
        ${printFooterStyles}

        .logo {
          position: absolute !important;
          top: 10px !important;
          left: 10px !important;
          z-index: 1000 !important;
        }
        .logo img { height: 60px !important; width: auto !important; }

        .header h3 {
          font-size: 1.5em !important;
          font-weight: bold !important;
          text-align: center !important;
          border-bottom: 1.5px solid #383838 !important;
          padding-bottom: 15px !important;
          margin-bottom: 10px !important;
          color: #000000 !important;
        }

        
        .header h6 {
          font-size: 1.2em !important;
          text-align: center !important;
          margin-bottom: 5px !important;
          font-weight: semibold !important;
          color: #000000 !important;
        }

        .client-row {
          display: flex !important;
          justify-content: space-between !important;
          margin-bottom: 8px !important;
          font-size: 1rem !important;
        }
        .client-label {
          font-weight: bold !important;
        }
        .underline {
          display: inline-block !important;
          border-bottom: 1px solid #000 !important;
          min-width: 140px !important;
          margin-left: 6px !important;
        }

        table {
          width: 100% !important;
          border-collapse: collapse !important;
          margin-top: 20px !important;
          font-size: 0.95rem !important;
        }
        th, td {
          border: 1px solid #000 !important;
          padding: 6px 8px !important;
          vertical-align: top !important;
        }
        th {
          background: #f8f9fa !important;
          font-weight: bold !important;
          text-align: center !important;
        }

        .footer {
          margin-top: 30px !important;
          border-top: 1.5px solid #000 !important;
          padding-top: 8px !important;
          font-size: 1rem !important;
        }
        .footer p { text-align: center !important; margin-bottom: 6px !important; }
        .footer-meta, .footer-sign {
          display: flex !important;
          justify-content: space-between !important;
          margin-top: 10px !important;
          font-size: 0.9rem !important;
        }

        @media print {
          .print-container { padding: 15px !important; }
        }
      `}</style>

      {/* ────────────────────── LOGO ────────────────────── */}
      <div className="logo">
        <img src={logo} alt="The Recovery House" />
      </div>

      {/* ────────────────────── MAIN CONTENT ────────────────────── */}
      <div className="print-container">
        <div className="header">
          <h3>THE RECOVERY HOUSE</h3>
          <h6>DEPOT & OTHER INJECTION CHART</h6>
        </div>

        {/* ────────────────────── CLIENT INFO (2 per row) ────────────────────── */}
        <div style={{ marginTop: "15px" }}>
          <div className="client-row">
            <div>
              <span className="client-label">Client's Name:</span>
              <span className="underline">{d.name || ""}</span>
            </div>
            <div>
              <span className="client-label">MR No:</span>
              <span className="underline">{d.mr_no || ""}</span>
            </div>
          </div>

          <div className="client-row">
            <div>
              <span className="client-label">DOB:</span>
              <span className="underline">{fmt(d.dob)}</span>
            </div>
            <div>
              <span className="client-label">Room No:</span>
              <span className="underline">{d.room_no || ""}</span>
            </div>
          </div>
        </div>

        {/* ────────────────────── TABLE ────────────────────── */}
        <table>
          <thead>
            <tr>
              <th style={{ width: "7%" }}>S.NO.</th>
              <th style={{ width: "15%" }}>Date / Time</th>
              <th style={{ width: "20%" }}>Injection Name</th>
              <th style={{ width: "5%" }}>MG.</th>
              <th style={{ width: "10%" }}>Route</th>
              <th style={{ width: "10%" }}>Duration</th>
              <th style={{ width: "15%" }}>Due Date</th>
              <th style={{ width: "18%" }}>Signature</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.id || i}>
                <td style={{ textAlign: "center" }}>{i + 1}</td>
                <td>{fmtDateTime(r.date_time)}</td>
                <td>{r.injection_name || ""}</td>
                <td style={{ textAlign: "center" }}>{r.mg ?? ""}</td>
                <td>{r.route || ""}</td>
                <td>{r.duration ?? ""}</td>
                <td>{fmt(r.due_date)}</td>
                <td>&nbsp;</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* ────────────────────── FOOTER ────────────────────── */}
        <div className="footer">
          <p>156 Bangalore Town, Tipu Sultan Road Karachi-Pakistan Ph : 021-34546364-65-66</p>
          <p>A Project of <strong>“CARVAN OF LIFE PAKISTAN TRUST”</strong></p>

        </div>
      </div>
    </PrintWrapper>
  );
}